<template>
  <v-navigation-drawer
    :key="brand.brand.value"
    ref="drawer"
    id="app-drawer"
    :value="drawer"
    app
    :color="brand.theme.primaryColor"
    floating
    mobile-breakpoint="768"
    width="240"
    style="height: calc(100vh - 64px); margin-top: 64px">
    <div class="sidebar-content-wrapper mb-8">
      <v-list-item three-line dark>
        <v-avatar size="34">
          <img src="../../assets/user.svg" alt />
        </v-avatar>
        <v-list-item-title class="ml-3">
          {{ email }}
          <div
            :style="{ color: brand.theme.userRole }"
            style="font-size: 14px; text-transform: capitalize"
            v-if="role">
            {{ role.toLowerCase() }}
          </div>
        </v-list-item-title>
      </v-list-item>

      <v-divider class="mx-2 mb-1" />
      <ul
        v-for="section in Object.keys(generateAvailableItems)"
        :style="{ color: brand.theme.sidebarColor }"
        class="sidebar-nav">
        <h6 class="section-title" :style="{ color: brand.theme.sidebarHeaders }">
          {{ section }}
        </h6>

        <li
          v-for="item in generateAvailableItems[section]"
          :key="item.name"
          :class="[
            $route.name === item.path ? 'selectedItem-item' : 'sidebar-main-link',
            { 'travel-rule-sidebar-item': item.name === 'Travel rule' },
          ]"
          @click="navigate(item.path)">
          <v-icon>{{ item.icon }}</v-icon>
          <v-list-item-title class="items-title">
            <div style="font-size: 14px; font-weight: normal">{{ item.name }}</div>
          </v-list-item-title>

          <v-badge
            v-if="item.name === 'Travel rule' && travelRuleBadgeCount && travelRuleBadgeCount > 0"
            class="tr-badge"
            color="error"
            inline
            :content="travelRuleBadgeCount" />
        </li>
      </ul>
    </div>
  </v-navigation-drawer>
</template>
<script>
import { mapActions, mapState } from "vuex";
import { ACTION_TOGGLE_SIDEBAR } from "@/store/types";
import { TokenService } from "@/services/storage.service";
import { FETCH_DEPOSITS_WITHOUT_TRAVEL_RULE_DATA } from "../../store/constants/travelRule";
import AppConfig from "@/config/app-config";

export default {
  name: "Sidebar",
  props: {
    drawer: Boolean,
  },
  data() {
    return {
      sidebarStructure: {
        Organisation: [
          {
            name: "Statistics",
            path: "statistics",
            icon: "$vuetify.icons.stats",
            permission: "GET_BALANCES",
          },
          {
            name: "Businesses",
            path: "businesses",
            icon: "$vuetify.icons.businesses",
            permission: "GET_BUSINESSES",
          },
          {
            name: "Bank accounts",
            path: "bank-accounts",
            icon: "$vuetify.icons.bank",
            permission: "FETCH_BANK_METADATA",
          },
          {
            name: "Whitelisted addresses",
            path: "whitelisted-addresses",
            icon: "$vuetify.icons.whitelistedAddresses",
            permission: "WHITELIST_ADDRESSES",
          },
          {
            name: "Withdrawal approvals",
            path: "withdrawal-approvals",
            icon: "$vuetify.icons.withdrawalApprovals",
            permission: "CREATE_WITHDRAWAL",
          },
          {
            name: "Travel rule",
            path: "travel-rule",
            icon: "$vuetify.icons.travelRule",
            permission: "GET_DEPOSITS_WITHOUT_TRAVEL_RULE",
          },
        ],
        Billing: [
          {
            name: "Crypto deposit",
            path: "crypto-deposit",
            icon: "$vuetify.icons.cryptoDeposit",
            permission: "CREATE_PAYMENT",
          },
          {
            name: "Crypto withdrawal",
            path: "crypto-withdrawal",
            icon: "$vuetify.icons.cryptoWithdrawal",
            permission: "CREATE_WITHDRAWAL",
          },
          {
            name: "Fiat deposit",
            path: "fiat-deposit",
            icon: "$vuetify.icons.fiatDeposit",
            permission: "CREATE_FIAT_DEPOSIT",
          },
          {
            name: "Fiat withdrawal",
            path: "fiat-withdrawal",
            icon: "$vuetify.icons.fiatWithdrawal",
            permission: "CREATE_FIAT_WITHDRAWAL",
          },
        ],
        "Balance management": [
          {
            name: "Internal transfer",
            path: "internal-transfer",
            icon: "$vuetify.icons.internalTransfer",
            permission: "CREATE_INTERNAL_TRANSFER",
          },
          {
            name: "Balance conversion",
            path: "balance-conversion",
            icon: "$vuetify.icons.balanceConversion",
            permission: "CREATE_BALANCE_CONVERSION",
          },
        ],
        Reporting: [
          {
            name: "Balance confirmation",
            path: "balance-confirmation",
            icon: "$vuetify.icons.wallet",
            permission: "GET_BALANCES",
          },
          {
            name: "Transaction ledger",
            path: "transaction-ledger",
            icon: "$vuetify.icons.ledger",
            permission: "GET_BALANCES",
          },
        ],
        Settings: [
          {
            name: "Change password",
            path: "change-password",
            icon: "$vuetify.icons.lock",
            permission: "",
          },
          {
            name: "Two-factor authentication",
            path: "authentication",
            icon: "$vuetify.icons.fingerprint",
            permission: "",
          },
          {
            name: "API keys",
            path: "api-keys",
            icon: "$vuetify.icons.api",
            permission: "GET_API_CREDENTIALS",
          },
          {
            name: "Users",
            path: "users",
            icon: "$vuetify.icons.user",
            permission: "CREATE_USER",
          },
          {
            name: "User roles",
            path: "user-roles",
            icon: "$vuetify.icons.group",
            permission: "MANAGE_ROLES",
          },
        ],
      },
      appId: "",
      inputValue: true,
    };
  },
  methods: {
    ...mapActions({
      toggleSidebar: ACTION_TOGGLE_SIDEBAR,
      fetchDeposits: FETCH_DEPOSITS_WITHOUT_TRAVEL_RULE_DATA,
    }),
    navigate(param) {
      if (this.$route.name === param) return;
      this.$router.push(`/${param}`).catch((err) => {});
    },
    determineAllowedRoutes(routeList) {
      return routeList.filter((link) => {
        const hasPermission = TokenService.getConfig("permissions").includes(link.permission);
        return link.path === 'fiat-deposit'
          ? AppConfig.loadedBrand?.fiatDepositAllowed && (link.permission === "" || hasPermission)
          : link.permission === "" ? true : hasPermission
      });
    },
  },
  computed: {
    ...mapState({
      brand: (state) => state.brand,
      role: (state) => state.user.role,
      userPermissions: (state) => state.user.permissions,
      email: (state) => state.user.email,
      isSidebarOpen: (state) => state.isSidebarOpen,
      orgId: (state) => state.user.organisationId,
      travelRuleBadgeCount: (state) => state.travelRule.deposits.length,
    }),
    generateAvailableItems() {
      const availableItems = [];
      Object.keys(this.sidebarStructure).forEach((section) => {
        const currentSectionItems = this.determineAllowedRoutes(this.sidebarStructure[section]);
        if (currentSectionItems.length) availableItems[section] = currentSectionItems;
      });
      return availableItems;
    },
  },
  watch: {
    isSidebarOpen: {
      immediate: true,
      handler(val) {
        this.inputValue = val;
      },
    },
  },
  async mounted() {
    await this.fetchDeposits();
  },
};
</script>
<style lang="scss" scoped>
.theme--light.v-divider {
  border-color: #456967 !important;
}

* {
  user-select: none;
}

.sidebar-main-link {
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  padding: 7px 25px 7px 23px;
  color: #fff;
  position: relative;
  display: flex;
  align-items: flex-end;

  &:hover {
    background: rgba(92, 92, 94, 0.452);
    width: 93%;
    cursor: pointer;
    border-top-right-radius: 23px;
    border-bottom-right-radius: 23px;
  }
}

.selectedItem-item {
  @extend .sidebar-main-link;
  background: rgba(92, 92, 94, 0.452);
  width: 93%;
  cursor: pointer;
  border-top-right-radius: 23px;
  border-bottom-right-radius: 23px;
}

.sidebar-nav {
  list-style: none;
  padding: 24px 0 0 0;
}

.section-title {
  padding: 0 0 12px 24px;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  display: flex;
  align-items: center;
}

.items-title {
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  margin-left: 10px;
}

.travel-rule-sidebar-item {
  align-items: center;
  position: relative;
}

:is(.sidebar-main-link, .selectedItem-item),
:is(.sidebar-main-link, .selectedItem-item):hover {
  width: 100%;
}

.travel-rule-sidebar-item .tr-badge {
  margin-left: auto;
  position: absolute;
  top: 50%;
  right: 5%;
  transform: translateY(-50%);
}
</style>
